import React, { useState } from "react";
import {
  Box,
  Paper,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  CircularProgress,
  Divider,
  IconButton,
  Alert,
  Grid2,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { observer } from "mobx-react";
import { Navigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import SendIcon from "@mui/icons-material/Send";

import CustomTypography from "../../components/common/form/CustomTypography";
import AppButton from "../../components/common/buttons/AppButton";
import FormTextField from "../../components/common/form/FormTextField";
import ImageUpload from "../../components/common/upload/ImageUpload";
import RichTextEditor from "../../components/common/textEditor/RichTextEditor";
import VoucherEmailPreview from "../../components/vouchers/VoucherEmailPreview";
import { useNotification } from "../../../utils/NotificationsContext";
import { useStores } from "../../../use-stores";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import {
  parseVoucherCsv,
  downloadVoucherCsvTemplate,
  formatVoucherCsvErrors,
  VoucherCsvValidationError,
} from "../../../utils/voucherEmailerCsvUtils";
import {
  VoucherCSVRow,
  EMAIL_TEMPLATES,
} from "../../../models/Vouchers/VoucherEmailer";
import EmailerAPI from "../../../api/EmailerAPI";

const emailerAPI = new EmailerAPI();

const STEPS = ["Upload CSV", "Compose Email", "Preview & Send"];

const SendVouchersEmailer: React.FC = observer(() => {
  const theme = useTheme();
  const { showError, showSuccess } = useNotification();
  const { userStore } = useStores();

  if (!userStore.hasPermission([EmployeeRoles.SEND_EMAILERS])) {
    return <Navigate to="/" replace />;
  }

  const [activeStep, setActiveStep] = useState(0);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<VoucherCSVRow[]>([]);
  const [csvErrors, setCsvErrors] = useState<VoucherCsvValidationError[]>([]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [logoFiles, setLogoFiles] = useState<File[]>([]);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  // const [testEmails, setTestEmails] = useState<string[]>([]);
  // const [testEmailInput, setTestEmailInput] = useState("");
  // const [isSendingTest, setIsSendingTest] = useState(false);
  const [csvFileName, setCsvFileName] = useState<string>("");
  const [selectedTemplate, setSelectedTemplate] = useState<string>("classic");
  const [hideSwageazyLogo, setHideSwageazyLogo] = useState<boolean>(false);

  const handleDownloadTemplate = () => {
    downloadVoucherCsvTemplate();
  };

  const handleCsvFileSelect = (files: File[]) => {
    if (files.length === 0) return;

    const file = files[0];
    setCsvFile(file);
    setCsvFileName(file.name);

    const reader = new FileReader();

    reader.onload = (e) => {
      const text = e.target?.result as string;
      const { data, errors } = parseVoucherCsv(text);

      setCsvData(data);
      setCsvErrors(errors);
    };

    reader.readAsText(file);
  };

  const handleCsvRemove = () => {
    setCsvFile(null);
    setCsvFileName("");
    setCsvData([]);
    setCsvErrors([]);
  };

  const handleLogoFileSelect = (files: File[]) => {
    if (files.length === 0) return;
    setLogoFiles(files);
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoUrl(reader.result as string);
    };
    reader.readAsDataURL(files[0]);
  };

  const handleLogoRemoveFromUpload = () => {
    setLogoUrl(null);
    setLogoFiles([]);
  };

  // const handleAddTestEmail = () => {
  //   const trimmedEmail = testEmailInput.trim();
  //   if (!trimmedEmail) return;

  //   const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  //   if (!emailRegex.test(trimmedEmail)) {
  //     showError("Error", "Invalid email format");
  //     return;
  //   }

  //   if (testEmails.includes(trimmedEmail)) {
  //     showError("Error", "Email already added");
  //     return;
  //   }

  //   setTestEmails([...testEmails, trimmedEmail]);
  //   setTestEmailInput("");
  // };

  // const handleRemoveTestEmail = (email: string) => {
  //   setTestEmails(testEmails.filter((e) => e !== email));
  // };

  // const handleSendTestEmail = async () => {
  //   if (testEmails.length === 0) {
  //     showError("Error", "Please add at least one test email");
  //     return;
  //   }

  //   if (!subject.trim()) {
  //     showError("Error", "Subject is required");
  //     return;
  //   }

  //   if (!message.trim()) {
  //     showError("Error", "Message is required");
  //     return;
  //   }

  //   if (!csvFile) {
  //     showError("Error", "Please upload a CSV file");
  //     return;
  //   }

  //   if (csvData.length === 0) {
  //     showError("Error", "No valid voucher data found");
  //     return;
  //   }

  //   try {
  //     setIsSendingTest(true);
  //     const response = await emailerAPI.sendTestVoucherEmail(
  //       csvFile,
  //       logoFiles.length > 0 ? logoFiles[0] : null,
  //       subject.trim(),
  //       message.trim(),
  //       selectedTemplate,
  //       hideSwageazyLogo,
  //       testEmails,
  //       csvData[0],
  //     );

  //     if (response.success) {
  //       showSuccess(response.message || "Test email sent successfully");
  //     } else {
  //       showError("Error", response.message || "Failed to send test email");
  //     }
  //   } catch (error: any) {
  //     showError(
  //       "Error",
  //       error?.response?.data?.message || "Failed to send test email",
  //     );
  //   } finally {
  //     setIsSendingTest(false);
  //   }
  // };

  const handleSendEmails = async () => {
    if (!subject.trim()) {
      showError("Error", "Subject is required");
      return;
    }

    const messageText = message.replace(/<[^>]*>/g, "").trim();
    if (!messageText) {
      showError("Error", "Message is required");
      return;
    }

    if (!csvFile) {
      showError("Error", "Please upload a CSV file");
      return;
    }

    if (csvData.length === 0) {
      showError("Error", "No valid recipients found");
      return;
    }

    try {
      setIsSending(true);
      const response = await emailerAPI.sendVoucherEmails(
        csvFile,
        logoFiles.length > 0 ? logoFiles[0] : null,
        subject.trim(),
        message,
        selectedTemplate,
        hideSwageazyLogo,
      );

      if (response.success) {
        showSuccess(
          response.message ||
          `Successfully sent emails to ${csvData.length} recipients`,
        );
        handleReset();
      } else {
        showError("Error", response.message || "Failed to send emails");
      }
    } catch (error: any) {
      showError(
        "Error",
        error?.response?.data?.message || "Failed to send emails",
      );
    } finally {
      setIsSending(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!csvFile) {
        showError("Error", "Please upload a CSV file");
        return;
      }
      if (csvErrors.length > 0) {
        showError(
          "Error",
          "Please fix CSV validation errors before proceeding",
        );
        return;
      }
      if (csvData.length === 0) {
        showError("Error", "No valid recipients found in CSV");
        return;
      }
    }

    if (activeStep === 1) {
      if (!subject.trim()) {
        showError("Error", "Subject is required");
        return;
      }
      if (!message.trim()) {
        showError("Error", "Message is required");
        return;
      }
    }

    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setCsvFile(null);
    setCsvFileName("");
    setCsvData([]);
    setCsvErrors([]);
    setSubject("");
    setMessage("");
    setLogoFiles([]);
    setLogoUrl(null);
    // setTestEmails([]);
    // setTestEmailInput("");
    setSelectedTemplate("classic");
    setHideSwageazyLogo(false);
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box
            sx={{
              maxWidth: "900px",
              margin: "0 auto",
            }}
          >
            <Box
              sx={{
                textAlign: "center",
                mb: 4,
                p: 1,
                backgroundColor: theme.palette.purple.main + "10",
                borderRadius: 2,
                border: "1px solid",
                borderColor: theme.palette.purple.main + "80",
              }}
            >
              <CustomTypography
                variant="heading5"
                fontWeight={700}
                sx={{ mb: 1, color: theme.palette.text.primary }}
              >
                Upload Recipients CSV
              </CustomTypography>
              <CustomTypography variant="body2" color="text.secondary">
                Download the template, fill in recipient details, and upload the
                CSV file
              </CustomTypography>
            </Box>

            <Paper
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: theme.palette.divider,
                borderRadius: 2,
                paddingX: 4,
                paddingY: 2,
                mb: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <CustomTypography variant="body2" fontWeight={600}>
                  Upload CSV File
                </CustomTypography>
                <AppButton
                  label="Download CSV Template"
                  startIcon={<DownloadIcon />}
                  variant="grayscale"
                  onClick={handleDownloadTemplate}
                  customStyles={{
                    minWidth: 180,
                    height: 28,
                  }}
                />
              </Box>
              <ImageUpload
                size="large"
                showImagesList={false}
                uploadText="Drop CSV file here or click to browse"
                addImagesToBeUploaded={handleCsvFileSelect}
                removeImage={handleCsvRemove}
                allowedFileTypes=".csv"
                fileInfo="Maximum file size: 10MB. Use the template format."
                disabled={false}
                newImageList={csvFile ? [csvFile] : []}
              />
              {csvFileName && (
                <Box
                  sx={{
                    mt: 2,
                    paddingX: 2,
                    paddingY: 1,
                    backgroundColor: theme.palette.grey[50],
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <CustomTypography variant="body2" fontWeight={600}>
                    {csvFileName}
                  </CustomTypography>
                  <IconButton size="small" onClick={handleCsvRemove}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Paper>

            {csvData.length > 0 && (
              <Alert
                severity="success"
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  "& .MuiAlert-message": {
                    width: "100%",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <CustomTypography variant="body2" fontWeight={600}>
                    Successfully parsed {csvData.length} valid recipient
                    {csvData.length !== 1 ? "s" : ""}
                  </CustomTypography>
                </Box>
              </Alert>
            )}

            {csvErrors.length > 0 && (
              <Box>
                <Alert
                  severity="error"
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                  }}
                >
                  Found {csvErrors.length} validation error
                  {csvErrors.length !== 1 ? "s" : ""}
                </Alert>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    backgroundColor: theme.palette.error.light + "15",
                    border: "1px solid",
                    borderColor: theme.palette.error.light,
                    borderRadius: 2,
                    maxHeight: "300px",
                    overflow: "auto",
                  }}
                >
                  {formatVoucherCsvErrors(csvErrors, 5).map((error, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        mb: 1.5,
                        "&:last-child": { mb: 0 },
                      }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          backgroundColor: theme.palette.error.main,
                          mt: 0.75,
                          mr: 1.5,
                          flexShrink: 0,
                        }}
                      />
                      <CustomTypography variant="body2" color="error">
                        {error}
                      </CustomTypography>
                    </Box>
                  ))}
                </Paper>
              </Box>
            )}
          </Box>
        );

      case 1:
        return (
          <Box
            sx={{
              maxWidth: "900px",
              margin: "0 auto",
            }}
          >
            <Box
              sx={{
                textAlign: "center",
                mb: 4,
                p: 1,
                backgroundColor: theme.palette.purple.main + "10",
                borderRadius: 2,
                border: "1px solid",
                borderColor: theme.palette.purple.main + "80",
              }}
            >
              <CustomTypography
                variant="heading4"
                fontWeight={700}
                sx={{
                  mb: 1,
                  color:
                    activeStep > 0
                      ? theme.palette.success.dark
                      : theme.palette.text.primary,
                }}
              >
                Compose Email
              </CustomTypography>
              <CustomTypography variant="body2" color="text.secondary">
                Customize your email subject, message, and branding
              </CustomTypography>
            </Box>

            <Paper
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: theme.palette.divider,
                borderRadius: 2,
                paddingX: 4,
                paddingY: 2,
                mb: 2,
              }}
            >
              <Box>
                <CustomTypography
                  variant="body2"
                  fontWeight={600}
                  sx={{ mb: 1 }}
                >
                  Subject *
                </CustomTypography>
                <FormTextField
                  label="Subject"
                  value={subject}
                  onChange={(id, value) => setSubject(value || "")}
                  placeholder="Enter email subject"
                  required
                  sx={{ mb: 2 }}
                />
              </Box>

              <Box>
                <CustomTypography
                  variant="body2"
                  fontWeight={600}
                  sx={{ mb: 1 }}
                >
                  Message *
                </CustomTypography>
                <RichTextEditor
                  value={message}
                  onChange={(value) => setMessage(value)}
                  placeholder="Enter your message to recipients..."
                  maxLength={700}
                  minHeight="200px"
                />
              </Box>

              <Divider sx={{ my: 4 }} />

              <Box>
                <CustomTypography
                  variant="body2"
                  fontWeight={600}
                  sx={{ mb: 1 }}
                >
                  Customer Logo (Optional)
                </CustomTypography>
                <CustomTypography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2, fontSize: "0.875rem" }}
                >
                  Upload a square or rectangular logo to personalize the email
                </CustomTypography>

                <ImageUpload
                  size="large"
                  showImagesList={true}
                  uploadText="Drop logo here or click to browse"
                  addImagesToBeUploaded={handleLogoFileSelect}
                  removeImage={handleLogoRemoveFromUpload}
                  allowedFileTypes="image/*"
                  fileInfo="Recommended: Square or rectangular logo (PNG, JPG, WEBP)"
                  disabled={false}
                  newImageList={logoFiles}
                  minHeight="150px"
                />

                {false && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mt: 2,
                      p: 2,
                      backgroundColor: theme.palette.grey[50],
                      borderRadius: 1,
                    }}
                  >
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <CustomTypography variant="body2" color="text.secondary">
                      Uploading logo...
                    </CustomTypography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>
        );

      case 2:
        return (
          <Box
            sx={{
              maxWidth: "900px",
              margin: "0 auto",
            }}
          >
            <Box
              sx={{
                textAlign: "center",
                mb: 4,
                p: 1,
                backgroundColor: theme.palette.purple.main + "10",
                borderRadius: 2,
                border: "1px solid",
                borderColor: theme.palette.purple.main + "80",
              }}
            >
              <CustomTypography
                variant="heading4"
                fontWeight={700}
                sx={{
                  mb: 1,
                  color:
                    activeStep > 1
                      ? theme.palette.success.dark
                      : theme.palette.text.primary,
                }}
              >
                Preview & Send
              </CustomTypography>
              <CustomTypography variant="body2" color="text.secondary">
                Review your email and send test emails before sending to all
                recipients
              </CustomTypography>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "400px 1fr" },
                gap: 3,
                mb: 3,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Paper
                  elevation={0}
                  sx={{
                    border: "1px solid",
                    borderColor: theme.palette.divider,
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <CustomTypography
                    variant="heading7"
                    fontWeight={600}
                    sx={{ mb: 2 }}
                  >
                    Select Email Template
                  </CustomTypography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    {EMAIL_TEMPLATES.map((template) => (
                      <Box
                        key={template.id}
                        onClick={() => setSelectedTemplate(template.id)}
                        sx={{
                          border: "2px solid",
                          borderColor:
                            selectedTemplate === template.id
                              ? theme.palette.primary.main
                              : theme.palette.divider,
                          borderRadius: 2,
                          paddingX: 2,
                          paddingY: 1,
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          backgroundColor:
                            selectedTemplate === template.id
                              ? theme.palette.primary.light + "10"
                              : "transparent",
                          "&:hover": {
                            borderColor: theme.palette.primary.main,
                            backgroundColor: theme.palette.primary.light + "05",
                          },
                        }}
                      >
                        <CustomTypography
                          variant="body2"
                          fontWeight={700}
                          sx={{
                            mb: 0.5,
                            color:
                              selectedTemplate === template.id
                                ? theme.palette.primary.main
                                : theme.palette.text.primary,
                          }}
                        >
                          {template.name}
                        </CustomTypography>
                        {/* <CustomTypography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: "0.875rem" }}
                        >
                          {template.description}
                        </CustomTypography> */}
                      </Box>
                    ))}
                  </Box>
                </Paper>
                <Paper
                  elevation={0}
                  sx={{
                    border: "1px solid",
                    borderColor: theme.palette.divider,
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <CustomTypography
                    variant="heading7"
                    fontWeight={600}
                    sx={{ mb: 2 }}
                  >
                    Email Summary
                  </CustomTypography>

                  <Box sx={{ mb: 2.5 }}>
                    <CustomTypography
                      variant="body2"
                      fontWeight={600}
                      style={{
                        color: theme.palette.customColors.grayText,
                        marginBottom: 4,
                      }}
                    >
                      Subject
                    </CustomTypography>
                    <CustomTypography variant="body2" fontWeight={600}>
                      {subject}
                    </CustomTypography>
                  </Box>

                  <Box sx={{ mb: 2.5 }}>
                    <CustomTypography
                      variant="body2"
                      fontWeight={600}
                      style={{
                        color: theme.palette.customColors.grayText,
                        marginBottom: 6,
                      }}
                    >
                      Recipients
                    </CustomTypography>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        backgroundColor: theme.palette.success.light + "20",
                        border: "1px solid",
                        borderColor: theme.palette.success.main,
                        borderRadius: 1,
                        px: 1.5,
                        py: 0.5,
                      }}
                    >
                      <CustomTypography
                        variant="body2"
                        fontWeight={700}
                        color="success.main"
                      >
                        {csvData.length}
                      </CustomTypography>
                    </Box>
                  </Box>

                  <Box>
                    <CustomTypography
                      variant="body2"
                      fontWeight={600}
                      style={{
                        color: theme.palette.customColors.grayText,
                        marginBottom: 4,
                      }}
                    >
                      Logo
                    </CustomTypography>
                    <CustomTypography variant="body2" fontWeight={600}>
                      {logoUrl ? "✓ Uploaded" : "No logo"}
                    </CustomTypography>
                  </Box>
                </Paper>

                {/* <Paper
                  elevation={0}
                  sx={{
                    border: "1px solid",
                    borderColor: theme.palette.divider,
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <CustomTypography
                    variant="heading7"
                    fontWeight={600}
                    sx={{ mb: 2 }}
                  >
                    Send Test Email
                  </CustomTypography>
                  <CustomTypography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, fontSize: "0.875rem" }}
                  >
                    Test the email before sending to all recipients
                  </CustomTypography>

                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      mb: 2,
                      alignContent: "center",
                    }}
                  >
                    <FormTextField
                      placeholder="Enter test email"
                      value={testEmailInput}
                      onChange={(id, value) => setTestEmailInput(value || "")}
                      sx={{ mb: 0 }}
                    />
                    <IconButton
                      onClick={handleAddTestEmail}
                      color="primary"
                      disabled={!testEmailInput.trim()}
                      sx={{
                        border: "1px solid",
                        borderColor: theme.palette.divider,
                        borderRadius: 1,
                        height: "100%",
                      }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>

                  {testEmails.length > 0 && (
                    <Box
                      sx={{
                        mb: 2,
                        p: 2,
                        backgroundColor: theme.palette.grey[50],
                        borderRadius: 1,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      {testEmails.map((email) => (
                        <Chip
                          key={email}
                          label={email}
                          onDelete={() => handleRemoveTestEmail(email)}
                          size="small"
                          sx={{
                            backgroundColor: "white",
                            border: "1px solid",
                            borderColor: theme.palette.divider,
                          }}
                        />
                      ))}
                    </Box>
                  )}

                  <AppButton
                    label={isSendingTest ? "Sending..." : "Send Test Email"}
                    startIcon={
                      isSendingTest ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <EmailIcon />
                      )
                    }
                    variant="grayscale"
                    onClick={handleSendTestEmail}
                    disabled={
                      testEmails.length === 0 || isSendingTest || isSending
                    }
                    customStyles={{ width: "100%" }}
                  />
                </Paper> */}

                <Paper
                  elevation={0}
                  sx={{
                    border: "1px solid",
                    borderColor: theme.palette.divider,
                    borderRadius: 2,
                    p: 3,
                  }}
                >
                  <CustomTypography
                    variant="heading6"
                    fontWeight={600}
                    sx={{ mb: 2 }}
                  >
                    Email Options
                  </CustomTypography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={hideSwageazyLogo}
                        onChange={(e) => setHideSwageazyLogo(e.target.checked)}
                        sx={{
                          color: theme.palette.primary.main,
                          "&.Mui-checked": {
                            color: theme.palette.primary.main,
                          },
                        }}
                      />
                    }
                    label={
                      <CustomTypography
                        variant="body2"
                        sx={{ fontSize: "0.9375rem" }}
                      >
                        Hide Swageazy logo from email
                      </CustomTypography>
                    }
                  />
                </Paper>
              </Box>

              <Box>
                <Paper
                  elevation={0}
                  sx={{
                    border: "1px solid",
                    borderColor: theme.palette.divider,
                    borderRadius: 2,
                    p: 2,
                    height: "100%",
                  }}
                >
                  <CustomTypography
                    variant="heading7"
                    fontWeight={600}
                    sx={{ mb: 2 }}
                  >
                    Email Preview
                  </CustomTypography>
                  <VoucherEmailPreview
                    logoUrl={logoUrl}
                    message={message}
                    sampleVoucher={csvData[0]}
                    template={selectedTemplate}
                    hideSwageazyLogo={hideSwageazyLogo}
                  />
                </Paper>
              </Box>
            </Box>
            <CustomTypography
              variant="body2"
              color="text.secondary"
              fontWeight={600}
              style={{ marginBottom: "0", fontStyle: "italic" }}
            >
              ** Make sure to send a test email to yourself before sending to
              all recipients.
            </CustomTypography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
      <Paper
        elevation={2}
        sx={{
          p: 2,
          borderRadius: 3,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            mb: 4,
          }}
        >
          <CustomTypography variant="heading3" fontWeight={700} sx={{ mb: 1 }}>
            Send Voucher Emails
          </CustomTypography>
        </Box>

        <Box
          sx={{
            maxWidth: "900px",
            margin: "0 auto",
            mb: 4,
          }}
        >
          <Stepper
            activeStep={activeStep}
            sx={{
              "& .MuiStepLabel-label": {
                fontSize: "0.8rem",
                fontWeight: 500,
              },
              "& .MuiStepLabel-label.Mui-active": {
                fontWeight: 700,
                fontSize: "1rem",
                color: theme.palette.purple.dark,
              },
              "& .MuiStepLabel-label.Mui-completed": {
                color: theme.palette.success.main,
                fontWeight: 600,
              },
              "& .MuiStepIcon-root": {
                fontSize: "1.2rem",
                fontWeight: 600,
              },
              "& .MuiStepIcon-root.Mui-completed": {
                color: theme.palette.success.main,
              },
              "& .MuiStepIcon-root.Mui-active": {
                fontSize: "1.5rem",
                color: theme.palette.purple.dark,
              },
            }}
          >
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Box sx={{ minHeight: "500px", mb: 2 }}>{renderStepContent()}</Box>

        <Divider sx={{ mb: 4 }} />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            maxWidth: "900px",
            margin: "0 auto",
          }}
        >
          <Box>
            {activeStep > 0 && (
              <AppButton
                label="Back"
                variant="grayscale"
                onClick={handleBack}
                disabled={isSending}
                customStyles={{ minWidth: 120, height: 44 }}
              />
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <AppButton
              label="Reset"
              variant="grayscale"
              onClick={handleReset}
              disabled={isSending}
              customStyles={{ minWidth: 120, height: 44 }}
            />

            {activeStep < STEPS.length - 1 ? (
              <AppButton
                label="Next"
                onClick={handleNext}
                disabled={
                  (activeStep === 0 &&
                    (!csvFile ||
                      csvErrors.length > 0 ||
                      csvData.length === 0)) ||
                  (activeStep === 1 &&
                    (!subject.trim() ||
                      !message.replace(/<[^>]*>/g, "").trim()))
                }
                customStyles={{ minWidth: 140, height: 44 }}
              />
            ) : (
              <AppButton
                label={isSending ? "Sending..." : "Send to All Recipients"}
                startIcon={
                  isSending ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <SendIcon />
                  )
                }
                onClick={handleSendEmails}
                disabled={isSending || csvData.length === 0}
                customStyles={{ minWidth: 220, height: 44 }}
              />
            )}
          </Box>
        </Box>
      </Paper>
    </Grid2>
  );
});

export default SendVouchersEmailer;
